import type { Bordereau, Mouvement } from '@mairistem/comptabilite';
import CODE_BHM from '../../../../constants/mouvements/codeBHM';
import NATURE_PIECE from '../../../../constants/mouvements/naturePiece';
import { TYPES_BORDEREAU_ANNULATION } from '../../../../constants/mouvements/typeBordereau';
import ActionResultType from '../../../../interfaces/pieces/ActionResultType';
import type { BaseLiquidationOptions } from '../../../../interfaces/pieces/mouvements/liquidation/BaseLiquidationOptions';
import MouvementActionType from '../../../../interfaces/pieces/mouvements/liquidation/MouvementActionType';
import type TreeviewActionResult from '../../../../interfaces/pieces/TreeviewActionResult';
import { annulerRejeterMouvement } from '../../../../services/piecesComptables/mouvements/mouvements';
import getMessageErreur from '../../../../utils/getMessageErreur';
import BaseLiquidationAction from '../../../pieces/BaseLiquidationAction';

/** Pièces qui en annulent une autre (06, 10, mêmes codes en recette) : elles ne s'annulent pas elles-mêmes. */
const NATURES_ANNULATION: string[] = [
	NATURE_PIECE.DEPENSE.ANNUL_REDUC,
	NATURE_PIECE.DEPENSE.ANNUL_MANDAT_RATT,
];

export interface AnnulationRejetData {
	/** Bordereau en brouillard choisi ou créé dans la modale. */
	identifiantBordereauDestination: number;
	reengager: boolean;
	/** Bordereau créé dans la modale, à ajouter à l'arbre avec la pièce. */
	bordereauCree?: Bordereau;
}

/**
 * Annulation (bordereau d'annulation 02) ou rejet (bordereau de rejet RE, estRejet) d'une pièce d'un bordereau
 * numéroté : la modale choisit ou crée le bordereau de destination en brouillard, l'API y crée la pièce
 * d'annulation ou de rejet, qui reprend le reste à annuler de chaque ligne.
 */
class MouvementAnnulerRejeterAction extends BaseLiquidationAction<
	Mouvement,
	MouvementActionType
> {
	private estRejet: boolean;

	constructor(options: BaseLiquidationOptions & { estRejet?: boolean } = null) {
		super(options);
		this.estRejet = options?.estRejet ?? false;
		this.label = this.estRejet ? 'Rejeter la pièce' : 'Annuler la pièce';
		this.type = this.estRejet
			? MouvementActionType.REJETER
			: MouvementActionType.ANNULER;
		this.isShown = (bordereau, mouvement) =>
			bordereau.codeBHM === CODE_BHM.VALUE.HISTORISE &&
			!TYPES_BORDEREAU_ANNULATION.includes(bordereau.typePes) &&
			!NATURES_ANNULATION.includes(mouvement?.naturePiece);
	}

	execute = (mouvement: Mouvement): TreeviewActionResult<Mouvement> => ({
		type: ActionResultType.MODAL,
		result: Promise.resolve(mouvement),
		extraProps: {
			mouvementAnnulationRejetModal: {
				visible: true,
				estRejet: this.estRejet,
				typeAction: this.type,
			},
		},
	});

	/**
	 * Pièce d'annulation ou de rejet ajoutée à son bordereau de destination, qui est un autre bordereau de l'arbre,
	 * ou un bordereau créé dans la modale.
	 */
	executeAction = (
		mouvement: Mouvement & AnnulationRejetData,
	): TreeviewActionResult<
		Mouvement & { isAnotherBordereau: boolean; isNewBordereau: boolean }
	> => ({
		type: ActionResultType.ADD,
		result: new Promise((resolve, reject) => {
			annulerRejeterMouvement(mouvement.identifiant as number, {
				identifiantBordereau: mouvement.identifiantBordereauDestination,
				willBeAnnulation: !this.estRejet,
				reengager: mouvement.reengager,
			})
				.then((pieceCreee) => {
					resolve({
						...pieceCreee,
						bordereau: mouvement.bordereauCree ?? pieceCreee.bordereau,
						isAnotherBordereau: true,
						isNewBordereau: !!mouvement.bordereauCree,
					});
				})
				.catch((e) => {
					reject(
						Error(
							getMessageErreur(
								e,
								this.estRejet
									? 'Erreur lors du rejet de la pièce'
									: "Erreur lors de l'annulation de la pièce",
							),
						),
					);
				});
		}),
		shouldRefreshChildren: true,
	});
}

export default MouvementAnnulerRejeterAction;
